<template>
  <div
    v-if="formReady"
    class="flex-row chemtracker-module"
    id="add-chemical-container-inventory"
  >
    <b-modal
      size="md"
      ref="chemSearchUnlinked"
      hide-backdrop
      centered
      hide-footer
      cancel-disabled
      ok-disabled
      @close="cancelAddUnlinkedChemical({ reason: 'cancel' })"
      id="chem-search-unlinked"
      :title="unlinkedModalTitle"
    >
      <div id="chem-search-form" class="d-block text-center">
        <unlinked-chemical-form
          :search-term="chemSearchValue"
          @unlinked-constituent-added="handleUnlinkedSelection"
          @cancel-add-unlinked-chemical="cancelAddUnlinkedChemical"
          @change-modal-title="(value) => (unlinkedModalTitle = value)"
        />
      </div>
    </b-modal>
    <b-modal
      v-if="chemSnapEnabled"
      ref="chemSnapUi"
      centered
      hide-footer
      cancel-disabled
      ok-disabled
      @close="closeChemSnapModal"
      no-close-on-backdrop
      hide-header
    >
      <ChemSnapUpload
        ref="chemSnapUpload"
        :chem-snap-config="chemSnapConfig"
        @close-modal="closeChemSnapModal"
        @upload-success="handleChemSnapData"
      />
    </b-modal>
    <b-form @submit.prevent="submitEntity">
      <v-teleport to="#page-title">
        <div class="header-buttons">
          <div>
            <v-button
              id="chemsnap-scan-with-camera"
              class="ghost primary xsmall"
              type="button"
              @click="showChemSnapModal"
              v-if="chemSnapEnabled"
              ><img :src="AiIconSrc" alt="chemSnap icon" />Add with Camera
            </v-button>
          </div>
          <DromoImportButton :is-ghost-button="true" v-if="showDromoButton" />
        </div>
      </v-teleport>
      <div v-for="fieldData in fields" :key="'fields-' + fieldData?.field_name">
        <chemical-search
          ref="chemAddInvSearch"
          v-if="fieldData?.field_name === 'field_chemical_type'"
          :field-config="fieldData"
          :required="true"
          :allow-delete="true"
          :options-above="true"
          :options-inline="true"
          :chemical-search-type-default="chemSearchDefaultType"
          :default-lookup-value="chemSearchValue"
          @chemical-lookup-clear="
            chemSearchOverride = {};
            formData.field_chemical_type = {};
          "
          :local-chemical="chemSearchOverride"
          @chemical-lookup-selection="
            (value) => handleChemicalLookupSelection(value)
          "
          :value="formData.field_chemical_type"
          :is-chemsnap-enabled="chemSnapEnabled"
          :chem-snap-data="chemSnapData"
          :horizontal-options="true"
          :show-search-input="showSearchInput[fieldData.field_name]"
          @toggle-show-search-input="
            () => toggleShowSearchInput(fieldData.field_name)
          "
          @search-unlinked-chemicals="searchUnlinkedChemicals"
        >
        </chemical-search>
        <entity-form-field
          v-if="
            !skipField(fieldData) &&
            !fieldInDetails(fieldData) &&
            fieldData?.form_display?.weight <= additionalDetailWeight
          "
          :ref="fieldData?.field_name"
          :key="fieldData?.field_name"
          :field-data="fieldData"
          :parent-value="formData[fieldData?.field_name]"
          entity-type="raft_metadata"
          bundle="chemical_container"
          :placeholder="getPlaceholder(fieldData?.field_name)"
          @form-error-message="catchFormError"
          @update-form-data="updateFormData"
          @validation-error="handleValidationError"
          :disabled="fieldDisabled(fieldData?.field_name)"
          :add-class="addClass"
          :autocomplete-formatter="fieldData?.autocompleteFormatter"
        />
        <raft-autocomplete
          v-if="fieldData?.field_name === 'og_audience' && !ogAudienceHidden"
          :key="fieldData?.field_name"
          field-class="entity-reference-field raft-og-audience"
          ref="groupAutocomplete"
          :id="fieldData?.field_name"
          :default-value="formData?.og_audience"
          :name="fieldData?.field_name"
          :label="fieldData?.label"
          @selected="(value) => updateFormData(value, 'og_audience')"
          element-id="raft-group-autocomplete"
          :results-display="formatGroupResultsDisplay"
          results-property="title"
          results-value="uuid"
          input-class="form-control"
          :show-label="true"
          :disabled="fieldDisabled(fieldData?.field_name)"
          @clear="updateFormData({}, 'og_audience')"
          :source="groupAutoCompleteResults"
          :field-required="true"
        />
        <single-select-filter
          ref="field_chemical_space"
          v-if="fieldData?.field_name === 'field_chemical_space'"
          :key="fieldData?.field_name"
          :get-value="buildingSpaces"
          id="owning-group-fil"
          data-test="owning-group-filter"
          :multiple="false"
          label="Location (Space):"
          display-value="title"
          :disable="spaceFilterDisabled"
          track-by="uuid"
          :selected-items="formData['field_chemical_space']"
          :update-method="
            (value) => updateFormData(value, 'field_chemical_space')
          "
          class="chem-add-inventory-location-field"
        />
        <div
          v-if="fieldData?.field_name === 'field_chemical_container_id'"
          id="unique-container-id"
        >
          <entity-form-field
            :ref="fieldData?.field_name"
            :key="fieldData?.field_name"
            :field-data="fieldData"
            :parent-value="formData[fieldData?.field_name]"
            entity-type="raft_metadata"
            bundle="chemical_container"
            :placeholder="getPlaceholder(fieldData?.field_name)"
            @form-error-message="catchFormError"
            @update-form-data="updateFormData"
            @validation-error="handleValidationError"
            :disabled="fieldDisabled(fieldData?.field_name)"
            :add-class="addClass"
            :autocomplete-formatter="fieldData?.autocompleteFormatter"
            :invalid="invalidInputError"
          />
        </div>
      </div>

      <div class="form-group">
        <label for="chem-container-count" class="vlabel raftjs-hovertip">
          Container Count:
        </label>
        <div class="raftjs-hovertip-icon">
          &nbsp;
          <b-img
            id="chem-container-count-hover"
            :src="helpSrc"
            alt="help"
            style="height: 0.75rem; padding-left: 0.5rem"
          />
          <b-popover target="chem-container-count-hover" triggers="hover focus">
            Indicate the number of containers to add. If a Unique ID number is
            provided, the containers will be created with sequential unique ID
            numbers.
          </b-popover>
        </div>
      </div>
      <input
        type="number"
        id="chem-container-count"
        ref="chem-container-count-inp"
        title="null"
        name="Container Count"
        data-test="Container Count"
        @input="updateFormData('container-count')"
        v-model="containerCount"
        :required="true"
        :max="50"
        :min="1"
        :step="1"
      />

      <details-field-group
        :collapsed="!chemSnapFoundAdditionalDetails"
        id="additional_details_collapse"
        label="Additional Details"
      >
        <template #content>
          <div
            v-for="fieldData in fields"
            :key="'details-fields-' + fieldData?.field_name"
          >
            <single-select-filter
              v-if="fieldData?.field_name === 'field_chemical_container_owner'"
              :key="fieldData?.field_name"
              :get-value="chemUsers"
              ref="field_chemical_container_owner"
              id="owner-fil"
              data-test="owner-filter"
              :multiple="false"
              :disable-empty-selection="false"
              label="Chemical Owner:"
              :disabled="fieldDisabled('field_chemical_container_owner')"
              track-by="uuid"
              :selected-items="formData['field_chemical_container_owner']"
              :update-method="
                (value) =>
                  updateFormData(value, 'field_chemical_container_owner')
              "
              class="chem-add-inventory-chem-owner-field"
            />
            <entity-form-field
              v-if="!skipField(fieldData) && fieldInDetails(fieldData)"
              :field-data="fieldData"
              :ref="fieldData?.field_name"
              :no-default-value="noDefaultValueField(fieldData?.field_name)"
              :parent-value="formData[fieldData?.field_name]"
              entity-type="node"
              bundle="chemical_container"
              :placeholder="getPlaceholder(fieldData?.field_name)"
              :disabled="fieldDisabled(fieldData?.field_name)"
              @form-error-message="catchFormError"
              @update-form-data="updateFormData"
              :add-class="addClassAdditional"
            />
          </div>
        </template>
      </details-field-group>

      <div v-for="fieldData in fields" :key="fieldData?.field_name">
        <entity-form-field
          v-if="
            !skipField(fieldData) &&
            !fieldInDetails(fieldData) &&
            fieldData?.form_display?.weight >= additionalDetailWeight
          "
          :field-data="fieldData"
          :parent-value="formData[fieldData?.field_name]"
          entity-type="node"
          bundle="chemical_container"
          :placeholder="getPlaceholder(fieldData?.field_name)"
          @form-error-message="catchFormError"
          :disabled="fieldDisabled(fieldData?.field_name)"
          @update-form-data="updateFormData"
          :add-class="addClass"
          :hidden="isConditionallyHidden(fieldData?.field_name)"
        />
        <b-form-checkbox
          v-if="fieldData?.field_name === 'field_chemical_dea_controlled'"
          :name="'checkbox-' + fieldData?.field_name"
          v-model="formData[fieldData?.field_name]"
          :id="fieldData?.field_name"
          @change="
            (value) => updateFormData(value, 'field_chemical_dea_controlled')
          "
          >{{ fieldData.label }}?
        </b-form-checkbox>
      </div>
      <div class="add-inventory-button-row">
        <template v-if="adding">
          <v-button
            :disabled="submitDisabled"
            type="submit"
            :class="buttonClasses('primary')"
          >
            Create
          </v-button>
          <v-button
            :disabled="submitDisabled"
            @click="addAnother"
            :class="buttonClasses('ghost primary')"
            data-test="add-another"
            :enable-prevent="true"
          >
            Create and Add Another
          </v-button>
        </template>
        <template v-else>
          <v-button
            :disabled="submitDisabled"
            @click.prevent="patchEntity()"
            :class="buttonClasses('primary')"
          >
            Update
          </v-button>
        </template>
      </div>
    </b-form>
    <recent-containers :context-nid="contextNid" :key="recentContainerIdKey" />
  </div>
</template>

<script>
import axios from "axios";
import { jsonClient, restClient } from "@/lib/raftApi";
import MetadataHelper from "@/lib/MetadataHelper";
import DetailsFieldGroup from "@/components/raft/DetailsFieldGroup.vue";
import { mapActions, mapGetters } from "vuex";
import SingleSelectFilter from "@/components/filters/SingleSelectFilter.vue";
import RaftAutocomplete from "@/components/raft/RaftAutocomplete.vue";
import ChemicalSearch from "@/components/raft/ChemicalSearch.vue";
import EntityFormHelper from "@/lib/EntityFormHelper";
import RecentContainers from "@/components/chemtracker/RecentContainers.vue";
import UnlinkedChemicalForm from "@/components/raft/UnlinkedChemicalForm.vue";
import VButton from "@/components/raft/VButton.vue";
import { useChemicalContainers } from "@/composables/useChemicalContainers";
import { useDrupalSettings } from "@/composables/useDrupalSettings";
import chemSnapIcon from "@/images/Automation-blue.svg";
import VTeleport from "@/components/raft/VTeleport.vue";
import raftGenericHelper from "../../lib/raft_generic_helper";
import EntityFormField from "../EntityFormField.vue";
import helpIcon from "../raft/help-Outline.svg";
import ChemSnapUpload from "./ChemSnapUpload.vue";
import ErrorStateLinks from "../raft/ErrorStateLinks.vue";
import AiIcon from "../../images/AI.svg";
import DromoImportButton from "./DromoImportButton.vue";

export default {
  name: "ChemicalContainerForm",
  data() {
    return {
      deserialized: {},
      errors: {},
      formReady: false,
      submitDisabled: false,
      spacesDisabled: false,
      ogAudienceHidden: false,
      showNoneSelectedModal: false,
      chemSearchValue: "",
      containerCount: 1,
      fields: [],
      amountRequired: false,
      unitOfMeasureRequired: false,
      chemSearchDefaultType: "suggest",
      chemSearchOverride: {},
      response: {},
      formData: {},
      helpSrc: helpIcon,
      chemSnapConfig: null,
      chemSnapData: {},
      chemSnapSrc: chemSnapIcon,
      defaultFormData: {
        og_audience: {},
        field_chemical_container_status: "Normal",
        field_chemical_type: null,
      },
      buildingSpaces: [],
      addClass: "chem-container-add-inv-field",
      addClassAdditional: "chem-container-add-inv-field-additional",
      additionalDetailWeight: 7,
      chemUsers: [],
      conditionalFields: {
        field_chemical_dea_contain_size: {
          controllingField: "field_chemical_dea_controlled",
          fieldId: "field_chemical_dea_contain_size",
          showValues: [true],
          state: "hidden",
        },
        field_chemical_dea_dosage_units_: {
          controllingField: "field_chemical_dea_controlled",
          fieldId: "field_chemical_dea_dosage_units_",
          showValues: [true],
          state: "hidden",
        },
        field_chemical_dea_finished_form: {
          controllingField: "field_chemical_dea_controlled",
          fieldId: "field_chemical_dea_finished_form",
          showValues: [true],
          state: "hidden",
        },
      },
      showSearchInput: {
        field_chemical_type: false,
        field_manufacturer: false,
      },
      chemicalContainerStatuses: {},
      unitsOfMeasure: {},
      saveAddAnotherFields: [
        "og_audience",
        "field_chemical_space",
        "field_chemical_container_status",
        "field_chemical_container_owner",
        "field_chemical_bench_location",
        "field_chemical_shelf_location",
        "field_chemical_room_location",
        "field_chemical_received",
        "field_chemical_expiration",
        "field_chemical_container_po",
        "field_chemical_note",
        "field_chemical_dea_controlled",
        "field_chemical_dea_contain_size",
        "field_chemical_dea_dosage_units_",
        "field_chemical_dea_finished_form",
      ],
      recentContainerIdKey: 0,
      disallowFormReset: false,
      unlinkedModalTitle: "Unlinked Chemicals",
      invalidInputError: false,
      AiIconSrc: AiIcon,
    };
  },
  methods: {
    ...mapActions(["fetchChemicalOwningUsers", "fetchLaboratoriesList"]),
    toggleShowSearchInput(fieldName) {
      this.showSearchInput[fieldName] = !this.showSearchInput[fieldName];
    },
    searchUnlinkedChemicals(value) {
      this.chemSearchValue = value;
      this.$refs.chemSearchUnlinked.show();
    },
    async fetchChemSnapConfig() {
      const data = await restClient
        .get({ path: "chemsnap_config", queries: {} })
        .catch((error) => {
          this.$showNotification("error", error);
          this.$showNotification("error", ErrorStateLinks);
        });
      this.$set(this, "chemSnapConfig", data);
    },
    handleValidationError(event) {
      this.errors = { ...this.errors, [event.field]: event.error };
    },
    async submitEntity(addAnother) {
      this.submitDisabled = true;
      if (this.errors?.field_chemical_manufacturer_vfield) {
        this.submitDisabled = false;
        return;
      }
      const data = EntityFormHelper.createJsonApiEntityData(
        "chemical_container",
        "chemical_container",
      );
      EntityFormHelper.fillJsonApiDataFromFormData(
        data,
        this.fields,
        this.formData,
      );
      data.attributes.title = "_placeholder_";
      if (data.relationships?.field_chemical_container_owner?.data) {
        data.relationships.field_chemical_container_owner.data = {
          type: "user--user",
          id: data.relationships.field_chemical_container_owner.data,
        };
      }
      if (data.relationships?.og_audience?.data) {
        data.relationships.og_audience.data = {
          type: "node--" + data.relationships.og_audience.data.type,
          id: data.relationships.og_audience.data.uuid,
        };
      }
      if (
        data.attributes?.field_chemical_expiration &&
        data.attributes?.field_chemical_expiration.length > 0
      ) {
        data.attributes.field_chemical_expiration[0].value =
          raftGenericHelper.formatDateFilterYyyyMmDd(
            new Date(data.attributes.field_chemical_expiration[0].value),
          );
      }
      if (
        data.attributes?.field_chemical_received &&
        data.attributes?.field_chemical_received.length > 0
      ) {
        data.attributes.field_chemical_received[0].value =
          raftGenericHelper.formatDateFilterYyyyMmDd(
            new Date(data.attributes.field_chemical_received[0].value),
          );
      }
      if (
        data.relationships?.field_chemical_manufacturer?.data?.selectedObject
      ) {
        data.relationships.field_chemical_manufacturer.data = {
          type: data.relationships.field_chemical_manufacturer.data
            .selectedObject.type,
          id: data.relationships.field_chemical_manufacturer.data.selectedObject
            .id,
        };
      }
      data.attributes.field_chemical_container_status =
        this.chemicalContainerStatuses[
          this.formData.field_chemical_container_status
        ];
      if (data.relationships?.field_chemical_type?.data?.id) {
        let chemDbUuid = "";
        const chemDbExists = await this.chemicalTypeExists(
          data.relationships.field_chemical_type.data.id[0],
        );
        if (typeof chemDbExists !== "object") {
          const chemDbResp = await this.chemicalTypePost(
            data.relationships.field_chemical_type.data.id[0],
            data.relationships.field_chemical_type.data.text,
          );
          chemDbUuid = chemDbResp.id;
        } else {
          chemDbUuid = chemDbExists.id;
        }
        data.relationships.field_chemical_type.data = {
          type: "node--chemdb_type",
          id: chemDbUuid,
        };
      }

      if (
        this.formData.field_chemical_type?.text !==
        this.formData.field_chemical_type?._source?.title?.[0]
      ) {
        data.attributes.field_chemical_name_preferred =
          this.formData.field_chemical_type?.text;
      }

      if (
        Object.hasOwn(this.formData, "field_chemsnap_response") &&
        this.formData.field_chemsnap_response.length
      ) {
        data.attributes.field_chemsnap_response =
          this.formData.field_chemsnap_response;
      }
      if (this.formData.field_chemical_container_photo?.id) {
        data.relationships.field_chemical_container_photo = {
          data: this.formData.field_chemical_container_photo,
        };
      }
      try {
        await this.postEntity(data, this.containerCount);
      } catch (e) {
        this.errorEmit(e.message, e.data);
      }
      this.submitDisabled = false;
      this.containerCount = 1;
      this.chemSnapData = {};
      if (!this.disallowFormReset) {
        if (addAnother !== true) {
          // Full reset the form for a normal submission.
          // Return here for reset form.
          this.resetForm();
          return;
        }
        this.invalidInputError = false;

        Object.keys(this.formData)?.forEach((key) => {
          if (!this.saveAddAnotherFields.includes(key)) {
            this.$set(this.formData, key, "");
          }
        });
        this.$refs.field_chemical_manufacturer?.[0].$refs?.field_chemical_manufacturer?.resetAutocomplete();
        this.$refs.chemAddInvSearch?.[0].clearSelected();
        this.updateConditionFields();
      }
      this.disallowFormReset = false;
    },
    resetForm() {
      // Full reset the form for a normal submission.
      this.$refs.field_chemical_space[0].selected = null;
      this.$refs.chemAddInvSearch[0].clearSelected();
      this.$refs.chemAddInvSearch[0]?.$refs.autocomplete?.resetAutocomplete();
      this.formData = JSON.parse(JSON.stringify(this.defaultFormData));
      this.$set(this.formData, "field_chemical_received", new Date());
      this.$refs.field_chemical_manufacturer[0].$refs.field_chemical_manufacturer.resetAutocomplete();
      this.$refs.field_chemical_container_owner[0].clearSelected();
      if (!this.contextNid) {
        this.$refs.groupAutocomplete[0].resetAutocomplete();
      } else {
        this.handleGroupLevel();
      }
      this.updateConditionFields();
      this.setFieldRequiredValue(
        "field_chemical_unit_of_measure",
        this.unitOfMeasureRequired,
      );
      this.setFieldRequiredValue("field_chemical_amount", this.amountRequired);
      if (this.invalidInputError) {
        this.invalidInputError = !this.invalidInputError;
      }
    },
    async chemicalTypeExists(uuid) {
      const queries = {};
      const chemDbData = await jsonClient
        .get({ path: "/node/chemdb_type/" + uuid, queries })
        .catch(() => ""); // 404 commonly expected. If entity doesn't exist we make it with a stub.
      return chemDbData.data;
    },
    async chemicalTypePost(uuid) {
      const stub = {
        type: "node--chemdb_type",
        id: uuid,
        attributes: {
          title: uuid,
          field_chemdb_physical_state: "C",
          field_chemdb_gdn: 999999999,
        },
      };
      const path = "node/chemdb_type";
      const response = await jsonClient
        .post({
          path,
          queries: this.groupContextQueries,
          data: stub,
        })
        .catch((e) => this.errorEmit(e));
      return response.data;
    },
    async patchEntity() {
      const jsonData = EntityFormHelper.createJsonApiEntityData(
        "chemical_container",
        "chemical_container",
      );
      EntityFormHelper.fillJsonApiDataFromFormData(
        jsonData,
        this.fields,
        this.formData,
      );
      const patchId = this.formData?.field_chemical_container_id?.trim();
      const errMsg = `Unable to save container ID: ${patchId}.`;
      const path = `node/chemical_container/${this.uuid}`;
      // eslint-disable-next-line no-await-in-loop
      const result = await jsonClient
        .patch({
          path,
          queries: this.groupContextQueries,
          data: jsonData,
        })
        .catch((e) => {
          this.handleContainerError(errMsg, e);
        });
      if (result && Object.hasOwn(result, "data")) {
        // In case no UUID is returned, increment default key to rerender table
        const containerUuid = result?.data?.id || this.recentContainerIdKey + 1;
        const containerId =
          result?.attributes?.field_chemical_container_id || "";
        this.showSuccessToast(containerUuid, containerId);
      }
    },
    async postEntity(jsonData, count) {
      let newId = this.formData?.field_chemical_container_id?.trim();
      for (let i = 0; i < count; i++) {
        if (
          i > 0 &&
          Object.hasOwn(jsonData.attributes, "field_chemical_container_id")
        ) {
          if (newId.length) {
            newId = raftGenericHelper.incrementLastNumber(newId);
          }
          jsonData.attributes.field_chemical_container_id[0].value = newId;
        }
        const errMsg = `Unable to save container ID: ${newId}.`;
        const path = "node/chemical_container";
        // eslint-disable-next-line no-await-in-loop
        const result = await jsonClient
          .post({
            path,
            queries: this.groupContextQueries,
            data: jsonData,
          })
          .catch((e) => this.handleContainerError(errMsg, e));
        if (result && Object.hasOwn(result, "data")) {
          const containerUuid = result?.data?.id;
          const containerId =
            result?.data?.attributes?.field_chemical_container_id;
          this.showSuccessToast(containerUuid, containerId);
        }
      }
      this.$emit("submit-form-data", "", {});
    },
    addAnother() {
      this.submitEntity(true);
    },
    catchFormError(error) {
      this.$emit("form-error-message", error);
    },
    handleContainerError(message, error) {
      // Specifically look for a duplicate id error and give it a more specific message consistent with B3.
      if (error?.response?.status === 422) {
        const detail = error?.response?.data?.errors?.[0]?.detail;
        if (detail) {
          const dupError = detail.match(
            /field_chemical_container_id.\d+: Content with unique container id (.*) already exists./,
          );
          const invalidIdError = detail.match(
            /field_chemical_container_id.0.value: This value is not valid./,
          );

          if (dupError?.[1]) {
            message = `Unique Container ID ${dupError[1]} already used. Please enter a different value or leave blank for an auto-generated ID.`;
            this.disallowFormReset = true;
            this.invalidInputError = true;
          }
          if (invalidIdError?.[0]) {
            message =
              "Invalid character found in Unique Container ID. Not allowed: `|,~\\";
            this.disallowFormReset = true;
            this.invalidInputError = true;
          }
        }
      }
      this.errorEmit(message, error);
    },
    errorEmit(message, data) {
      this.$showNotification("error", message);
      this.$emit("form-error-message", { message: message, data: data });
    },
    updateFormData(value, field) {
      this.$emit("update-form-data", {
        value: value,
        field: field,
      });
      if (field === "og_audience") {
        // Clear location & previous group when Group Name is selected
        this.$set(this.formData, "field_chemical_space", "");
        this.$set(this.formData, field, {});
        this.$refs.field_chemical_space[0].selected = null;
        this.spacesDisabled = true;
        this.buildingSpaces = [];
        // Set Group Name and update location list
        this.$set(this.formData, field, value.selectedObject);
        this.updateSpaceOptions(value.selectedObject);
        this.fetchGroupContainerOwners(value.selectedObject);
        return;
      }
      if (
        typeof value === "object" &&
        Object.hasOwn(value, "field_name") &&
        value.field_name === "field_chemical_amount"
      ) {
        this.setFieldRequiredValue(
          "field_chemical_unit_of_measure",
          !!value.field_value,
        );
      }
      // Ensure we have a valid selection for chemical manufacturer.  If not, clear the field.
      if (field === "field_chemical_manufacturer") {
        if (typeof value === "string") {
          this.$nextTick(() =>
            this.$delete(this.formData, "field_chemical_manufacturer"),
          );
        }
      }
      if (
        typeof value === "object" &&
        Object.hasOwn(value, "field_name") &&
        value.field_name === "field_chemical_unit_of_measure"
      ) {
        this.setFieldRequiredValue(
          "field_chemical_amount",
          !!value.field_value,
        );
      }
      // Display unlinked modal if USER clicks on:
      // "Search unlinked chemicals" because there are no matches
      if (field === "field_chemical_type") {
        if (value.value === "_none_") {
          this.$set(this, "chemSearchValue", value.search);
          this.showNoneSelectedModal = true;
          this.$refs.chemSearchUnlinked.show();
        }
        if (value?.searchType === "product") {
          // Expected result elsewhere is an array with 1 element.
          if (!Array.isArray(value.selectedObject.id)) {
            value.selectedObject.id = [value.selectedObject.id];
          }
          this.handleChemicalFoundByProduct(value.selectedObject);
        }
        this.$set(this.formData, field, value.selectedObject);
        return;
      }
      if (field) {
        this.$set(this.formData, field, value);
        this.updateConditionFields();
        return;
      }
      this.$set(this.formData, value.field_name, value.field_value);
      this.invalidInputError = false;
    },
    async updateSpaceOptions(group) {
      if (!this.formData.og_audience) {
        return;
      }
      const response = await restClient.get({
        path: "/spaces",
        queries: { related_organizations: [group.nid] },
      });
      this.buildingSpaces = response.spaces;
      this.spacesDisabled = false;
    },
    getPlaceholder: function (fieldName) {
      const fieldConfig = this.fields.find(
        (item) => item?.field_name === fieldName,
      );
      if (fieldConfig) {
        return fieldConfig?.form_display?.settings?.placeholder;
      }
      return MetadataHelper.getPlaceholder(this.getMetadataType, fieldName);
    },
    fetchFields: async function () {
      const options = {
        path: "/fields/node/chemical_container",
        queries: {},
      };
      this.response = await restClient
        .get(options)
        .catch((e) => this.errorEmit(e.message, e.data));
      const bundleFieldsSorted = [];
      Object.values(this.response)?.forEach((fieldData) => {
        if (fieldData?.form_display?.type === "chemical_container_id") {
          fieldData.field_type = "string";
        }
        if (fieldData?.field_name === "field_chemical_note") {
          fieldData.field_type = "string_long";
        }
        if (fieldData?.field_name === "field_chemical_type") {
          fieldData.field_type = "entity_reference";
        }
        if (fieldData?.field_name === "field_chemical_manufacturer") {
          fieldData.field_type = "entity_reference";
          fieldData.showLabel = false;
          fieldData.autoCompleteSource = this.manufacturerLookup;
          fieldData.autocompleteFormatter = this.manufacturerFormatter;
        }
        if (fieldData?.field_name === "field_chemical_container_owner") {
          fieldData.form_display.type = "entity_reference";
        }
        if (fieldData?.field_name === "field_chemical_container_owner") {
          fieldData.form_display.type = "entity_reference";
        }
        if (fieldData?.field_name === "field_chemical_container_status") {
          fieldData.field_storage.settings.allowed_values?.forEach(
            (value, index) => {
              this.chemicalContainerStatuses[value] = index;
            },
          );
        }
        if (fieldData?.field_name === "field_chemical_unit_of_measure") {
          Object.entries(
            fieldData.field_storage.settings.allowed_values,
          )?.forEach(([key, value]) => {
            this.unitsOfMeasure[value] = key;
          });
          this.unitOfMeasureRequired = fieldData.required;
        }
        if (fieldData?.field_name === "field_chemical_amount") {
          this.amountRequired = fieldData.required;
        }
        if (fieldData?.form_displays?.default) {
          bundleFieldsSorted.push(fieldData);
        }
      });
      bundleFieldsSorted.sort(
        (a, b) =>
          a.form_displays.default.weight - b.form_displays.default.weight,
      );
      this.additionalDetailWeight =
        this.response?.third_party_settings?.default?.field_group?.group_chemical_additional_detail?.weight;
      this.fields = bundleFieldsSorted;
    },
    skipField(field) {
      // Details: https://bioraft.atlassian.net/wiki/spaces/DEVEL/pages/4036624411/Chemical+Container+Form#Skipped-fields
      const skipFields = [
        "revision_log",
        "field_chemical_space",
        "og_audience",
        "field_chemical_type",
        "field_chemical_container_owner",
        "field_chemical_dea_controlled",
        "field_chemical_container_id",
      ];
      return skipFields.includes(field?.field_name);
    },
    fieldInDetails(field) {
      const childFields =
        this.response?.third_party_settings?.default?.field_group
          ?.group_chemical_additional_detail?.children;
      return childFields.includes(field?.field_name);
    },
    formatGroupResultsDisplay(result) {
      if (result?.operative_status !== "1") {
        return result.title + " (Inactive)";
      }
      return result?.title;
    },
    groupAutoCompleteResults(search) {
      const autocomplete = this.$refs.groupAutocomplete[0];
      autocomplete.setResults([]);
      const results = this.allLabs.filter((labItem) =>
        labItem.title.toLowerCase().includes(search.toLowerCase()),
      );
      autocomplete.setResults([]);
      autocomplete.setResults(results);
    },
    async manufacturerLookup(input, resultFunc) {
      const queries = {
        "fields[chemdb_manufacturer--chemdb_manufacturer]": "id,name",
        "filter[name][condition][path]": "name",
        "filter[name][condition][value]": input,
        "filter[name][condition][operator]": "CONTAINS",
      };
      const path = "/chemdb_manufacturer/chemdb_manufacturer";
      try {
        const manufacturerData = await jsonClient
          .cancelRequest(path)
          .get({ path, queries });
        const results = manufacturerData?.deserialized;
        resultFunc(results);
      } catch (e) {
        if (!axios.isCancel(e)) {
          this.errorEmit(`Failed to find any manufacturers. ${e}`);
        }
      }
    },
    manufacturerFormatter(value) {
      return value.name ?? value;
    },
    async fetchGroupContainerOwners(group) {
      if (!this.formData.og_audience) {
        return;
      }
      const queries = {
        "fields[user--user]":
          "drupal_internal__uid,name,field_prof_display_name,display_name",
        "filter[entity_id][condition][path]": "entity_id",
        "filter[entity_id][condition][value]": group.nid,
        "filter[state]": "active",
        "filter[uid.status]": 1,
        include: "uid",
        sort: "uid.field_prof_display_name,uid.name",
      };
      const groupOwners = await jsonClient
        .getAllResults({ path: "/og_membership/default", queries })
        .catch((e) => this.errorEmit(`Failed to get chemical owners. ${e}`));
      this.chemUsers = [];
      groupOwners?.forEach((chemUser) => {
        this.chemUsers.push({
          uuid: chemUser.uid.id,
          title: chemUser.uid.field_prof_display_name,
        });
      });
    },
    fieldDisabled(fieldName) {
      if (
        !this.formData?.field_chemical_type ||
        !Object.hasOwn(this.formData?.field_chemical_type, "id")
      ) {
        return true;
      }
      return !!(
        fieldName === "field_chemical_container_owner" && this.chemUsers
      );
    },
    isConditionallyHidden(fieldName) {
      // The field is not hidden if it's not defined as a conditional field.
      if (!this.conditionalFields.hasOwnProperty(fieldName)) {
        return false;
      }
      return this.conditionalFields[fieldName].state === "hidden";
    },
    updateConditionFields() {
      Object.values(this.conditionalFields)?.forEach((value) => {
        if (
          Object.hasOwn(this.formData, value.controllingField) &&
          value.showValues.includes(this.formData[value.controllingField])
        ) {
          this.conditionalFields[value.fieldId].state = "visible";
          this.setFieldRequiredValue(value.fieldId, true);
        } else {
          this.conditionalFields[value.fieldId].state = "hidden";
          this.setFieldRequiredValue(value.fieldId, false);
        }
      });
    },
    setFieldRequiredValue(fieldName, required) {
      for (let i = 0; i < this.fields.length; i++) {
        if (this.fields[i].field_name === fieldName) {
          this.fields[i].required = required;
        }
      }
    },
    cancelAddUnlinkedChemical() {
      this.$refs.chemSearchUnlinked.hide();
    },
    handleUnlinkedSelection(value) {
      const data = {};
      if (Object.hasOwn(value, "id")) {
        data.id = [value.id];
        data.title = value.title;
        data.value = value.id;
        data.field_chemdb_physical_state = value.field_chemdb_physical_state;
      }
      if (Object.hasOwn(value, "selectedObject")) {
        data.id = [value.value["0"]];
        data.title = value.selectedObject.selectedObject?._source?.title?.[0];
        data.value = value.value["0"];
        data.field_chemdb_physical_state =
          value.selectedObject.selectedObject?._source?.field_chemdb_physical_state?.[0];
      }
      this.updateFormData({ selectedObject: data }, "field_chemical_type");
      this.chemSearchOverride = data;
      this.$refs.chemSearchUnlinked.hide();
    },
    noDefaultValueField(field) {
      return field === "field_chemical_expiration";
    },
    handleChemicalFoundByProduct(chemObj) {
      this.updateFormData({
        field_name: "field_chemical_product_number",
        field_value: chemObj._source?.product_number[0],
      });
      this.updateFormData({
        field_name: "field_chemical_product_name",
        field_value: chemObj._source?.product_name[0],
      });
      const manuObject = {
        type: "chemdb_manufacturer--chemdb_manufacturer",
        name: chemObj._source?.manufacturer_name[0],
        id: chemObj._source?.manufacturer_uuid[0],
      };
      this.updateFormData({
        field_name: "field_chemical_manufacturer",
        field_value: {
          selectedObject: manuObject,
          display: manuObject.name,
          value: manuObject.id,
        },
      });
      this.$refs?.field_chemical_manufacturer?.[0]?.updateValue(manuObject);
    },
    async handleGroupLevel() {
      const foundGroup = this.allLabs.find(
        (labElement) => labElement.nid === this.contextNid,
      );
      const groupObj = {
        selectedObject: foundGroup,
        display: foundGroup?.title,
        value: foundGroup?.uuid,
      };
      raftGenericHelper.setPageTitle("Add Chemicals to " + groupObj.display);
      this.$nextTick(() => {
        this.$refs?.groupAutocomplete?.[0]?.updateValue(foundGroup);
        this.updateFormData(groupObj, "og_audience");
        this.ogAudienceHidden = true;
      });
    },
    showChemSnapModal() {
      this.$refs.chemSnapUi.show();
    },
    closeChemSnapModal() {
      this.$refs.chemSnapUi.hide();
    },
    checkCasNumberFormat(input) {
      // CAS Number format XXXXXXX-YY-Z
      const regex = /\d+-\d+-\d+/i;
      return regex.test(input);
    },
    setChemicalName(data) {
      this.$set(this, "chemSearchDefaultType", "suggest");
      this.$set(this, "chemSearchValue", data["Chemical name"]);
      const autocomplete = this.$refs.chemAddInvSearch[0].$refs.autocomplete;
      autocomplete.pickFirst = true;
      autocomplete.updateSearch(data["Chemical name"]);
    },
    setCasNumber(data) {
      this.$set(this, "chemSearchDefaultType", "cas_number");
      this.$set(this, "chemSearchValue", data["CAS number"]);
      const autocomplete = this.$refs.chemAddInvSearch[0].$refs.autocomplete;
      autocomplete.pickFirst = true;
      autocomplete.updateSearch(data["CAS number"]);
    },
    handleChemSnapData({ data, uploadedImage }) {
      this.resetForm();
      this.$set(this, "chemSnapData", data);
      const keys = ["CAS number", "Chemical name"];
      keys?.forEach((key) => {
        if (Object.hasOwn(data, key)) {
          data[key] = data[key].replace(
            /Chemical name or CAS number: null/g,
            "",
          );
          data[key] = data[key].replace(/mical name or CAS number: null/g, "");
          data[key] = data[key].replace(/CAS number:/g, "");
          data[key] = data[key].replace(/Chemical name:/g, "");
        }
      });

      // When data is received from the ChemSnap API, update the form data fields.
      // Try CAS number first, then chemical name.
      if (data["CAS number"] && data["CAS number"] !== "null") {
        // Check if CAS number received is in correct CAS number format
        const dataCasNumber = data["CAS number"].trim();
        const isRightFormat = this.checkCasNumberFormat(dataCasNumber);
        if (isRightFormat) {
          this.setCasNumber(data);
        } else {
          this.setChemicalName(data);
        }
      } else {
        if (data["Chemical name"] === "") {
          this.$showNotification(
            "info",
            "Unable to find CAS Number or Chemical Name. Fill out the form manually or try a different image.",
          );
          return;
        }
        this.setChemicalName(data);
      }
      if (Object.hasOwn(data, "Lot number")) {
        this.updateFormData({
          field_name: "field_chemical_lot_number",
          field_value: data["Lot number"],
        });
      }
      if (Object.hasOwn(data, "Amount")) {
        const number = parseFloat(data.Amount);
        this.updateFormData({
          field_name: "field_chemical_amount",
          field_value: number,
        });
      }
      this.matchUnits(data);
      this.updateFormData(data["raw output"], "field_chemsnap_response");
      const options = {
        entityType: "node",
        entityBundle: "chemical_container",
        fieldName: "field_chemical_container_photo",
        queries: this.groupContextQueries,
        files: [uploadedImage],
      };
      jsonClient.postFilesToField(options).then((results) => {
        if (results[0]?.data?.id) {
          const photoData = {
            id: results[0].data.id,
            type: "file--file",
          };
          this.updateFormData(photoData, "field_chemical_container_photo");
        }
      });
      this.closeChemSnapModal();
    },
    matchUnits(data) {
      // Case insensitive comparison.
      if (Object.hasOwn(data, "Units")) {
        const input = data.Units.toLowerCase();
        for (const [key, value] of Object.entries(this.unitsOfMeasure)) {
          if (key.toLowerCase() === input) {
            this.updateFormData({
              field_name: "field_chemical_unit_of_measure",
              field_value: value,
            });
            break;
          }
        }
      }
    },
    handleChemicalLookupSelection(value) {
      this.updateFormData(value, "field_chemical_type");
      this.updateFormData(
        this.chemSnapData["Product name"],
        "field_chemical_product_name",
      );
      if (this.chemSnapData["Product number"] !== "null") {
        this.updateFormData(
          this.chemSnapData["Product number"],
          "field_chemical_product_number",
        );
      }
      if (
        typeof this.chemSnapData.Manufacturer !== "undefined" &&
        this.chemSnapData.Manufacturer !== "null"
      ) {
        this.updateFormData(
          this.chemSnapData.Manufacturer,
          "field_chemical_manufacturer",
        );
        const autocomplete =
          this.$refs.field_chemical_manufacturer?.[0].$refs
            ?.field_chemical_manufacturer;
        autocomplete.pickFirst = true;
        autocomplete.updateSearch(this.chemSnapData.Manufacturer);
      }
      if (value?.searchType === "product") {
        this.handleChemicalFoundByProduct(value.selectedObject);
        document.querySelector("#additional_details_collapse").open = true;
      }
    },
    buttonClasses(classes) {
      return this.submitDisabled ? `${classes} disabled` : classes;
    },
    forceRerender(recentContainerId) {
      this.recentContainerIdKey = recentContainerId;
    },
    showSuccessToast(uuid, id) {
      this.forceRerender(uuid);
      this.$showNotification("success", `Container ${id} created`);
    },
  },
  props: {
    contextNid: { type: String, default: null },
    uuid: {
      type: String,
      default: "",
    },
  },
  computed: {
    ...mapGetters(["allLabs"]),
    showDromoButton() {
      return this.config.isDromoContext;
    },
    chemSnapFoundAdditionalDetails() {
      const keys = Object.keys(this.chemSnapData);
      return (
        keys.includes("Lot number") ||
        keys.includes("Product name") ||
        keys.includes("Product number")
      );
    },
    adding() {
      return !this.uuid;
    },
    groupContextQueries() {
      const queries = {};
      if (this.contextNid) {
        queries["rog-type"] = "node";
        queries["rog-id"] = this.contextNid;
      }
      return queries;
    },
    spaceFilterDisabled: function () {
      return (
        this.spacesDisabled ||
        !this.formData?.og_audience?.hasOwnProperty("nid")
      );
    },
    chemSnapEnabled: function () {
      // Cast to integer so that Array.includes() works as expected.
      const currentUserUid = Number.parseInt(this.config.currentUser, 10);
      if (this.chemSnapConfig?.enabled) {
        // Admin should always see the feature if it's enabled.
        if (currentUserUid === 1) {
          return true;
        }
        // An empty enabled_for array means the feature is enabled for all users.
        if (!this.chemSnapConfig.enabled_for.length) {
          return true;
        }
        // Check to see if the current user's uid is in the array of user ids in the Chemsnap config.
        return this.chemSnapConfig.enabled_for.includes(currentUserUid);
      }
      return false;
    },
    getGroupType() {
      let groupType = "";
      if (!this.$isB4) {
        groupType = Drupal?.settings?.bioraft?.group_type_machine_name;
      } else {
        groupType = drupalSettings?.chemtracker?.context_type;
      }
      return groupType;
    },
  },
  setup(props) {
    const { fetchContainerByUuid } = useChemicalContainers(props.contextNid);
    const { config } = useDrupalSettings();
    return { fetchContainerByUuid, config };
  },
  async created() {
    this.formData = JSON.parse(JSON.stringify(this.defaultFormData));
    await Promise.all([
      this.fetchChemSnapConfig(),
      this.fetchChemicalOwningUsers(),
      this.fetchLaboratoriesList(),
    ]);
    let containerResponse = null;
    if (this.uuid) {
      containerResponse = await this.fetchContainerByUuid(this.uuid);
      this.deserialized = { ...containerResponse?.deserialized };
      const chemicalType = containerResponse?.deserialized?.field_chemical_type;
      const adjustedChemicalType = {
        text: chemicalType?.title,
        _index: "chemsourcedb_chemicals",
        _source: {
          field_chemdb_physical_state:
            chemicalType?.field_chemdb_physical_state,
          title: [chemicalType?.title],
          uuid: [chemicalType?.id],
        },
        id: chemicalType?.id,
      };
      const ogAudience = containerResponse?.deserialized?.og_audience?.[0];
      const adjustedOgAudience = {
        nid: ogAudience?.drupal_internal__nid,
        operative_status: ogAudience?.operative_status,
        title: ogAudience?.title,
        type: ogAudience?.type?.slice(6),
        uuid: ogAudience?.id,
      };
      containerResponse.deserialized = {
        ...containerResponse.deserialized,
        field_chemical_note:
          containerResponse?.data?.attributes?.field_chemical_note?.value,
        field_chemical_container_owner: [
          containerResponse?.data?.relationships?.field_chemical_container_owner
            ?.uuid,
        ],
        field_chemical_manufacturer: containerResponse?.deserialized
          ?.field_chemical_manufacturer
          ? {
              ...containerResponse.deserialized.field_chemical_manufacturer,
              selectedObject:
                containerResponse?.deserialized?.field_chemical_manufacturer,
            }
          : null,
        field_chemical_type: adjustedChemicalType,
        og_audience: adjustedOgAudience,
      };
    }
    await this.fetchFields();
    if (this.uuid) {
      // Skipped fields https://bioraft.atlassian.net/wiki/spaces/DEVEL/pages/4036624411/Chemical+Container+Form+V4+Add+Inventory
      const skip = ["revision_log"];
      this.fields?.forEach((field) => {
        if (
          !skip.includes(field.field_name) &&
          containerResponse?.deserialized?.[field.field_name]
        ) {
          this.updateFormData(
            containerResponse?.deserialized?.[field.field_name],
            field.field_name,
          );
        }
        this.$refs.chemAddInvSearch[0].updateSelected(
          containerResponse?.deserialized.field_chemical_type,
          () => {},
        );
        this.$refs.chemAddInvSearch[0].value =
          containerResponse?.deserialized.field_chemical_type;
        this.$refs.groupAutocomplete[0].setResults([
          containerResponse?.deserialized.og_audience,
        ]);
        this.$refs.groupAutocomplete[0].select(
          containerResponse?.deserialized.og_audience,
        );
      });
    }
    this.formReady = true;
    if (this.contextNid) {
      await this.handleGroupLevel();
    }
  },
  components: {
    UnlinkedChemicalForm,
    VButton,
    RecentContainers,
    ChemicalSearch,
    RaftAutocomplete,
    SingleSelectFilter,
    DetailsFieldGroup,
    EntityFormField,
    ChemSnapUpload,
    VTeleport,
    DromoImportButton,
  },
  provide() {
    return {
      isB4: this.$isB4,
    };
  },
};
</script>

<style scoped>
.chemical-search-by-label > label {
  display: none;
}

.chem-add-inventory-location-field {
  margin-top: 0.5rem;
}

#add-chemical-container-inventory #chemical-search-wrapper,
#add-chemical-container-inventory .raft-autocomplete-wrapper {
  margin-bottom: 0.8rem;
}

#chem-container-count {
  margin-bottom: 0.25rem;
}

.add-inventory-button-row {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
}

#add-chemical-container-inventory #chemical-search-wrapper {
  display: grid;
  grid-template-columns: max-content 1fr;
  width: 100%;
  align-items: center;
}

.header-buttons {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.125rem;
}

body.fullwidth .header-buttons {
  justify-content: flex-start;
  gap: 1rem;
}

@container (width < 300px) {
  .header-buttons button.scishield-button {
    font-size: var(--font-size-75);
    padding: 0 1rem;
  }

  .header-buttons {
    justify-content: center;
    flex-wrap: wrap;
    container-type: inline-size;
  }
}

div.raftjs-hovertip,
div.raftjs-hovertip div,
div.raftjs-hovertip label {
  display: inline-block !important;
}
</style>

<style>
.raft-autocomplete-wrapper {
  width: auto;
  max-width: 400px;
}

.field-label {
  font-weight: bold;
  color: #333;
}

#page-title {
  display: flex;
  padding: 0.125rem;
}

#center-column #page-title:has(.header-buttons) {
  display: grid;
  grid-template-columns: fit-content(85%) max-content;
}

#center-column #page-title:has(.header-buttons .dromo-import-button) {
  grid-template-columns: fit-content(45%) 1fr;
}
</style>
